/**
 * Role checking utilities
 * 
 * Centralized role checking logic to prevent fragility and inconsistencies
 * across the codebase.
 */

import { UserProfileSession } from '../../store/slices/userProfileSlice';
import { authLogger } from '../../utils/logger';

/**
 * Valid user roles in the system
 */
export enum UserRole {
  ADMIN = 'admin',
  AGENT = 'agent',
  OWNER = 'owner'
}

/**
 * Check if a user is an admin
 * 
 * Admin permissions are granted to users with role 'admin' or 'owner'
 * 
 * @param userProfile - The user profile session from Redux state
 * @returns true if the user has admin permissions, false otherwise
 */
export function isAdmin(userProfile: UserProfileSession | null | undefined): boolean {
  if (!userProfile) {
    authLogger.debug('[roleChecks] No user profile provided, user is not admin');
    return false;
  }

  if (!userProfile.role) {
    authLogger.warn('[roleChecks] User profile has no role field', {
      userId: userProfile.id,
      email: userProfile.email
    });
    return false;
  }

  const role = userProfile.role.toLowerCase().trim();
  const hasAdminPermissions = role === UserRole.ADMIN || role === UserRole.OWNER;

  authLogger.debug('[roleChecks] Admin check result', {
    userId: userProfile.id,
    email: userProfile.email,
    role: userProfile.role,
    normalizedRole: role,
    isAdmin: hasAdminPermissions
  });

  return hasAdminPermissions;
}

/**
 * Check if a user has a specific role
 * 
 * @param userProfile - The user profile session from Redux state
 * @param targetRole - The role to check for
 * @returns true if the user has the specified role, false otherwise
 */
export function hasRole(
  userProfile: UserProfileSession | null | undefined,
  targetRole: UserRole | string
): boolean {
  if (!userProfile || !userProfile.role) {
    return false;
  }

  const userRole = userProfile.role.toLowerCase().trim();
  const checkRole = typeof targetRole === 'string' 
    ? targetRole.toLowerCase().trim() 
    : targetRole;

  return userRole === checkRole;
}

/**
 * Check if a user has any of the specified roles
 * 
 * @param userProfile - The user profile session from Redux state
 * @param roles - Array of roles to check
 * @returns true if the user has any of the specified roles, false otherwise
 */
export function hasAnyRole(
  userProfile: UserProfileSession | null | undefined,
  roles: (UserRole | string)[]
): boolean {
  if (!userProfile || !userProfile.role) {
    return false;
  }

  const userRole = userProfile.role.toLowerCase().trim();

  return roles.some(role => {
    const checkRole = typeof role === 'string' 
      ? role.toLowerCase().trim() 
      : role;
    return userRole === checkRole;
  });
}

/**
 * Get a user-friendly display name for a role
 * 
 * @param role - The role to get a display name for
 * @returns A formatted display name
 */
export function getRoleDisplayName(role: string | undefined): string {
  if (!role) return 'Unknown';

  switch (role.toLowerCase()) {
    case UserRole.ADMIN:
      return 'Admin';
    case UserRole.OWNER:
      return 'Owner';
    case UserRole.AGENT:
      return 'Agent';
    default:
      return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  }
}

